// frontend/src/pages/doctor/DoctorAIAnalysis.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, AlertCircle, ChevronDown, ChevronUp, Clipboard } from 'lucide-react';
import api from '../../api/axios';

interface PrescriptionSuggestion {
  patient_summary: string;
  S: string;
  AP: string;
  warnings: string[];
  candidates?: string[];   // 当院登録の処方候補（AI未使用モード）
}

interface AIAnalysis {
  status: 'pending' | 'processing' | 'done' | 'error';
  suggestion?: PrescriptionSuggestion | null;
  model_used?: string;
  error_message?: string;
  feedback_diagnosis?: string | null;
  feedback_accuracy?: string | null;
  created_at?: string;
}

interface Props {
  reservationId: number;
  /** S/AP を左パネルへ適用するコールバック */
  onApplyToRecord?: (s: string, ap: string) => void;
}

const DoctorAIAnalysis: React.FC<Props> = ({ reservationId, onApplyToRecord }) => {
  const [data, setData] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  const pollCountRef = React.useRef(0);
  const MAX_POLLS = 150; // 最大150回（4秒×150 = 10分）

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await api.get<AIAnalysis>(`/doctor/questionnaire/${reservationId}/ai`);
      setData(res.data);
      if (res.data.status === 'processing' || res.data.status === 'pending') {
        if (pollCountRef.current < MAX_POLLS) {
          pollCountRef.current += 1;
          setTimeout(fetchAnalysis, 4000);
        } else {
          setData({ status: 'error', error_message: 'AI分析がタイムアウトしました。再分析してください。' });
        }
      }
    } catch {
      setData({ status: 'error', error_message: 'データの取得に失敗しました' });
    } finally {
      setLoading(false);
    }
  }, [reservationId]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const handleRetry = async () => {
    setRetrying(true);
    setApplied(false);
    pollCountRef.current = 0;
    try {
      await api.post(`/doctor/questionnaire/${reservationId}/ai/analyze`);
      setData({ status: 'processing' });
      setTimeout(fetchAnalysis, 3000);
    } finally {
      setRetrying(false);
    }
  };

  const handleApply = () => {
    if (!data?.suggestion || !onApplyToRecord) return;
    onApplyToRecord(data.suggestion.S, data.suggestion.AP);
    setApplied(true);
  };

  // ── ローディング ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        <Loader2 className="animate-spin w-5 h-5 mr-2" />
        <span className="text-sm">読み込み中...</span>
      </div>
    );
  }

  // ── pending / processing ──
  if (!data || data.status === 'pending' || data.status === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-3 text-gray-400">
        <Loader2 className="animate-spin w-6 h-6" />
        <span className="text-sm">AI分析を生成中...</span>
        <span className="text-xs text-gray-300">問診票提出後、数秒で完了します</span>
      </div>
    );
  }

  // ── エラー ──
  if (data.status === 'error') {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-2 text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">AI分析エラー</p>
            <p className="text-xs text-red-400 mt-0.5">{data.error_message}</p>
          </div>
        </div>
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
          再分析する
        </button>
      </div>
    );
  }

  // ── done ──
  const sug = data.suggestion;
  // ルールベース(AI未使用)かどうか — 免責文言を実態に合わせて切り替える
  const isRuleBased = (data.model_used || '').includes('ルールベース');

  return (
    <div className="text-sm space-y-3 px-3 py-3">

      {/* 免責文言（生成元に応じて表記を変える） */}
      <p className="text-xs text-gray-400 bg-gray-50 rounded px-2 py-1.5 leading-relaxed">
        {isRuleBased
          ? '問診票からの自動要約です（AI未使用）。医師の判断を優先してください。'
          : '⚠️ AIの提案です。医師の判断を優先してください。'}
      </p>

      {sug ? (
        <>
          {/* 患者サマリー */}
          {sug.patient_summary && (
            <div className="bg-blue-50 rounded px-2.5 py-2">
              <span className="text-xs font-bold text-blue-600 mr-1">患者：</span>
              <span className="text-xs text-blue-800">{sug.patient_summary}</span>
            </div>
          )}

          {/* S：主訴・経緯 */}
          {sug.S && (
            <section>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                S（主訴・経緯）
              </h3>
              <div className="bg-gray-50 rounded px-2.5 py-2">
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{sug.S}</p>
              </div>
            </section>
          )}

          {/* AP：処方推奨 */}
          {sug.AP && (
            <section>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                AP（処方推奨）
              </h3>
              <div className="bg-green-50 border border-green-100 rounded px-2.5 py-2">
                <p className="text-xs text-green-800 whitespace-pre-wrap leading-relaxed font-medium">{sug.AP}</p>
              </div>
            </section>
          )}

          {/* 処方候補（当院登録薬・AI未使用モード）— 医師が選択して処方 */}
          {sug.candidates && sug.candidates.length > 0 && (
            <section>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                処方候補（当院登録薬）
              </h3>
              <ul className="space-y-1">
                {sug.candidates.map((c, i) => (
                  <li key={i} className="text-xs text-gray-700 bg-gray-50 rounded px-2 py-1">
                    {c}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-gray-400 mt-1">※医師が選択して処方してください</p>
            </section>
          )}

          {/* 注意事項 */}
          {sug.warnings && sug.warnings.length > 0 && (
            <section>
              <button
                onClick={() => setWarningsOpen(v => !v)}
                className="flex items-center gap-1 text-xs font-bold text-amber-600 mb-1"
              >
                {warningsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                注意事項 ({sug.warnings.length})
              </button>
              {warningsOpen && (
                <ul className="space-y-1">
                  {sug.warnings.map((w, i) => (
                    <li key={i} className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                      ⚠ {w}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* 診療録へ適用ボタン */}
          {onApplyToRecord && (
            <div className="border-t border-gray-100 pt-2">
              {applied ? (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  ✓ 診療録へ反映しました
                </p>
              ) : (
                <button
                  onClick={handleApply}
                  className="flex items-center gap-1.5 text-xs bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 transition-colors"
                >
                  <Clipboard className="w-3 h-3" />
                  診療録へ適用
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-gray-400 text-center py-6">AI提案がありません</p>
      )}

      {/* 再分析 + モデル表示 */}
      <div className="flex items-center justify-between border-t border-gray-100 pt-2">
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
          再分析
        </button>
        {data.model_used && (
          <span className="text-xs text-gray-300">{data.model_used}</span>
        )}
      </div>
    </div>
  );
};

export default DoctorAIAnalysis;
